import { BehaviorSubject, combineLatest, map, Subject } from 'rxjs';
import type { GeometryState, LatLngPoint } from '@/shared/types';
import { computeZones } from '../utils/zone-calculator';

const INITIAL: GeometryState = { takeoffPoint: null, flightArea: [], additionalTakeoffPoints: [] };

export interface ZoneSettings {
  bufferMeters: number;
  terminationMeters: number;
}

const DEFAULT_ZONE_SETTINGS: ZoneSettings = { bufferMeters: 10, terminationMeters: 50 };

function createMapService() {
  const geometry$ = new BehaviorSubject<GeometryState>(INITIAL);
  const flyTo$ = new Subject<{ lat: number; lng: number; zoom?: number }>();
  const zoneSettings$ = new BehaviorSubject<ZoneSettings>(DEFAULT_ZONE_SETTINGS);
  const activeDrawingTool$ = new BehaviorSubject<'marker' | 'additional-marker' | 'polygon' | null>(null);
  const polygonError$ = new BehaviorSubject<string | null>(null);

  const zones$ = combineLatest([geometry$, zoneSettings$]).pipe(
    map(([{ flightArea }, { bufferMeters, terminationMeters }]) =>
      flightArea.length >= 3 ? computeZones(flightArea, { bufferMeters, terminationMeters }) : null
    ),
  );

  return {
    geometry$,
    zones$,
    zoneSettings$,
    activeDrawingTool$,
    polygonError$,
    flyTo$: flyTo$.asObservable(),
    setTakeoffPoint: (point: LatLngPoint | null) =>
      geometry$.next({ ...geometry$.value, takeoffPoint: point }),
    setFlightArea: (points: LatLngPoint[]) =>
      geometry$.next({ ...geometry$.value, flightArea: [...points] }),
    setAdditionalTakeoffPoints: (points: LatLngPoint[]) =>
      geometry$.next({ ...geometry$.value, additionalTakeoffPoints: [...points] }),
    addAdditionalTakeoffPoint: (point: LatLngPoint) =>
      geometry$.next({
        ...geometry$.value,
        additionalTakeoffPoints: [...geometry$.value.additionalTakeoffPoints, point],
      }),
    setZoneSettings: (settings: Partial<ZoneSettings>) =>
      zoneSettings$.next({ ...zoneSettings$.value, ...settings }),
    setActiveDrawingTool: (tool: 'marker' | 'additional-marker' | 'polygon' | null) =>
      activeDrawingTool$.next(tool),
    setPolygonError: (msg: string | null) =>
      polygonError$.next(msg),
    flyTo: (lat: number, lng: number, zoom = 15) => flyTo$.next({ lat, lng, zoom }),
    reset: () => {
      geometry$.next(INITIAL);
      zoneSettings$.next(DEFAULT_ZONE_SETTINGS);
      activeDrawingTool$.next(null);
      polygonError$.next(null);
    },
  };
}

export const mapService = createMapService();
